import * as particles from "pixi-particles";
import {EndlessHover} from "slotworks/graphics/animation/base-animations";
import {AbstractSceneTransition} from "slotworks/graphics/layers/scene-transitions/abstract-scene-transition";
import {CenterPivot, Ungroup} from "slotworks/graphics/pixi/group";
import Contract from "slotworks/utils/contract";
import Point from "slotworks/utils/geom/point";
import {parallel, sequence} from "slotworks/utils/race-utils";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";
import Components from "../../../components/components";
import {BounceZoomIn, BounceZoomOut} from "../../../graphics/animation/base-animations";
import Layers from "../../../graphics/layers/layers";

export default class RevolverWinTransitions extends AbstractSceneTransition {

    protected static coinShower: particles.Emitter;
    protected static gemShower: particles.Emitter;
    protected static blastLeft: particles.Emitter;
    protected static blastRight: particles.Emitter;
    protected static explodeEmitter: particles.Emitter;

    protected static background: PIXI.Sprite;
    protected static winValue: PIXI.extras.BitmapText;
    protected static winText: PIXI.Sprite;

    protected static winTextHover: TweenJS.Tween;

    public BOUNCE_TIME: number = 700;
    public HOVER_SCALE: number = 1.15;
    public HOVER_TIME: number = 1000;
    public BOTTOM_LINE_DELAY: number = 100;

    public BLAST_DURATION: number = 750;
    public EXPLODE_DELAY: number = 500;
    public EXPLODE_DURATION: number = 750;

    public TEXT_DELAY: number = 500;

    protected EXPLOSION_OFFSET = 200;

    protected EXPLOSION_TIME: number;
    protected timers: number[] = [];

    protected zoomGroup: PIXI.Container;

    protected inResolve: Function;

    protected resetEmitter(emitter: particles.Emitter) {
        emitter.emit = false;
        emitter.cleanup();
    }

    protected clearTimers() {
        if (this.timers) {
            for (const timer of this.timers) {
                Timer.clearTimeout(timer);
            }
            this.timers = [];
        }
    }

    protected resolveIn() {
        if (this.inResolve) {
            this.inResolve();
        }

        this.inResolve = null;
    }

    protected explode(gems: boolean = false) {
        RevolverWinTransitions.coinShower.emit = false;
        RevolverWinTransitions.gemShower.emit = false;

        this.resetEmitter(RevolverWinTransitions.blastLeft);
        this.resetEmitter(RevolverWinTransitions.blastRight);

        RevolverWinTransitions.blastLeft.emit = true;
        RevolverWinTransitions.blastRight.emit = true;

        Timer.setTimeout(() => {
            RevolverWinTransitions.blastLeft.emit = false;
            RevolverWinTransitions.blastRight.emit = false;
        }, this.BLAST_DURATION);

        Timer.setTimeout(() => {
            this.resetEmitter(RevolverWinTransitions.explodeEmitter);
            RevolverWinTransitions.explodeEmitter.emit = true;
            Timer.setTimeout(() => {
                RevolverWinTransitions.explodeEmitter.emit = false;
            }, this.EXPLODE_DURATION);
        }, this.EXPLODE_DELAY);

        Timer.setTimeout(() => {
            RevolverWinTransitions.coinShower.emit = true;
            if (gems) {
                RevolverWinTransitions.gemShower.emit = true;
            }
        }, this.EXPLODE_DELAY + this.EXPLODE_DURATION);
    }

    protected explodeAgain(gems: boolean = false) {
        if (Components.bigWin.upgradePending()) {
            this.explode(gems);
        }
    }

    protected endlessHover(topItem: PIXI.Container) {
        EndlessHover(CenterPivot(topItem, null, new Point(0.5, 1)), this.HOVER_SCALE, this.HOVER_TIME);
        RevolverWinTransitions.winTextHover = EndlessHover(CenterPivot(RevolverWinTransitions.winText, null, new Point(0.5, 1)), this.HOVER_SCALE, this.HOVER_TIME, this.BOTTOM_LINE_DELAY);
    }

    protected zoomTextIn(endScale: number, duration: number): Contract<void> {
        const target = RevolverWinTransitions.winValue;
        TweenJS.removeAll(target.scale);
        return Contract.wrapTween(new TweenJS.Tween(target.scale).to({x: endScale, y: endScale}, duration));
    }

    protected setTextSize(scale: number) {
        const target = RevolverWinTransitions.winValue;
        TweenJS.removeAll(target.scale);
        target.scale.x = target.scale.y = scale;
    }

    protected introduce(target: PIXI.Container, duration: number, pivot: Point, startSize: number = 0) {
        return sequence([
            () => this.zoom(target, startSize, 1.2, duration * 0.75, TweenJS.Easing.Sinusoidal.Out, pivot),
            () => this.zoom(target, 1.2, 1, duration * 0.25, TweenJS.Easing.Sinusoidal.Out, pivot)
        ]);
    }

    protected groupAndAnimateOut(target: PIXI.Container) {
        return new Contract<void>((resolve) => {
            try {
                sequence([
                    () => Contract.wrap(() => {
                        Layers.CelebrationForeground.add(target, false, true);
                        this.zoomGroup = CenterPivot([target, RevolverWinTransitions.winText], "zoomGroup");
                        TweenJS.removeAll(RevolverWinTransitions.winValue.scale);
                        TweenJS.removeAll(RevolverWinTransitions.winValue);
                    }),
                    () => parallel([
                        () => BounceZoomOut(this.zoomGroup, this.BOUNCE_TIME),
                        () => this.zoom(RevolverWinTransitions.winValue, RevolverWinTransitions.winValue.scale.x, 0, this.BOUNCE_TIME, TweenJS.Easing.Back.In)
                    ]),
                    () => Contract.wrap(() => {
                        Ungroup(this.zoomGroup, "zoomGroup");
                        Layers.CelebrationContent.add(target, false, false);

                        this.zoomGroup = null;
                    })
                ]).then(() => resolve(null));

            } catch (error) {
                target.visible = false;
                resolve(null);
            }
        });
    }

    private zoom(target: PIXI.Container,
                 scaleFrom: number, scaleTo: number, duration: number, easing: (k: number) => number,
                 pivot: Point = null): Contract<void> {

        return new Contract<void>((resolve) => {

            const group = CenterPivot(target, null, pivot);

            group.scale.set(scaleFrom, scaleFrom);

            const tween = new TweenJS.Tween(target.scale)
                .to({x: scaleTo, y: scaleTo}, duration)
                .easing(easing);
            tween.onComplete(() => {
                tween.onComplete(null);
                Ungroup(group);
                target.scale.set(scaleTo, scaleTo);
                resolve(null);
            });
            tween.start();
        });
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/integration/revolver/transitions/revolver-win-transitions.ts